import React, { useState, useRef, useEffect } from "react";
import api from "../api/api";
import "bootstrap/dist/css/bootstrap.min.css";
import { LanguageService } from "./languageService";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Bot,
  X,
  ArrowLeft,
  ThumbsUp,
  ThumbsDown,
  MessageCircle,
  Send,
  Volume2,
  Square,
  Loader2
} from "lucide-react";
import i18n, { t } from "i18next";
import "../css/Chatbot.css";
import AssistantAttachment from "./assistant/AssistantAttachment";
import AssistantVoice from "./assistant/AssistantVoice";
import { isVideo, mediaCopy } from "./assistant/assistantMedia";
import { sendMessage, notifyAssistantUpdated } from "./assistant/assistantApi";
import { countingErrorMessage } from "./assistant/countingPresentation";

const Chatbot = ({ setIsOpen, isOpen }) => {
  const [userLanguage, setUserLanguage] = useState("en");
  const [messages, setMessages] = useState([]);
  const [uiMessages, setUiMessages] = useState(() =>
    LanguageService.getUIMessages("en")
  );
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const messagesEndRef = useRef(null);

  const [inputText, setInputText] = useState("");
  const [selectedImages, setSelectedImages] = useState([]);
  const localMediaUrls = useRef(new Set());
  useEffect(() => {
    const urls = localMediaUrls.current;
    return () => { urls.forEach((url) => URL.revokeObjectURL(url)); urls.clear(); };
  }, []);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [voiceIds, setVoiceIds] = useState([]);
  const [botLoadingMessage, setBotLoadingMessage] = useState(false);
  const [sendError, setSendError] = useState(null);

  // --- Text-to-speech: read a bot answer aloud ---
  const [ttsLoadingId, setTtsLoadingId] = useState(null);
  // The drawer continues the most recent conversation so it shares history with
  // the full-page assistant instead of keeping a separate flat list.
  const [conversationUid, setConversationUid] = useState(null);
  const [speakingId, setSpeakingId] = useState(null);
  const speechAudioRef = useRef(null);
  const speechUrlRef = useRef(null);

  // Initialize with welcome message and update UI messages
  useEffect(() => {
    setUiMessages(LanguageService.getUIMessages(userLanguage));
  }, [userLanguage]);

  const toggleChatbot = () => {
    setIsOpen(!isOpen);
  };

  const handleInputChange = (e) => {
    setInputMessage(e.target.value);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load the most recent conversation on mount (shared with /assistant).
  useEffect(() => {
    api
      .get("/assistant/conversations")
      .then((res) => {
        const latest = (res.data.conversations || [])[0];
        if (!latest) return { data: { messages: [] } };
        setConversationUid(latest.uid);
        return api.get(`/assistant/conversations/${latest.uid}/messages`);
      })
      .then((response) => {
        setMessages((response && response.data && response.data.messages) || []);
      setTimeout(() => scrollToBottom(), 500);
    });
  }, []);

  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => (document.body.style.overflow = '');
  }, [isOpen]);

  // History rows come back with `messageId`; live ones carry `id`.
  const messageKey = (msg, idx) => msg.messageId ?? msg.id ?? idx;

  // `userLanguage` above is never updated (always "en"), so read the live UI
  // locale instead: i18next first, then the value the language switcher stores.
  const currentLanguage = () =>
    (i18n.language || localStorage.getItem("local") || "en")
      .split("-")[0]
      .toLowerCase();

  const stopSpeaking = () => {
    if (speechAudioRef.current) {
      speechAudioRef.current.pause();
      speechAudioRef.current = null;
    }
    if (speechUrlRef.current) {
      URL.revokeObjectURL(speechUrlRef.current);
      speechUrlRef.current = null;
    }
    setSpeakingId(null);
  };

  // Never let audio outlive the chat window.
  useEffect(() => stopSpeaking, []);

  const handleSpeak = async (msg, idx) => {
    const key = messageKey(msg, idx);

    // Clicking the speaker of the message already playing stops it.
    if (speakingId === key) {
      stopSpeaking();
      return;
    }
    stopSpeaking();

    const text = msg.text || msg.content;
    if (!text) return;

    try {
      setTtsLoadingId(key);
      const response = await api.post(
        "/chatbot/speech",
        { text, language: currentLanguage() },
        { responseType: "blob" }
      );

      const url = URL.createObjectURL(response.data);
      speechUrlRef.current = url;

      const audio = new Audio(url);
      speechAudioRef.current = audio;
      audio.onended = stopSpeaking;
      audio.onerror = stopSpeaking;

      setSpeakingId(key);
      await audio.play();
    } catch (error) {
      console.error("Text-to-speech failed:", error);
      stopSpeaking();
    } finally {
      setTtsLoadingId(null);
    }
  };

  const selectMedia = (file) => {
    selectedImages.forEach((item) => { URL.revokeObjectURL(item.url); localMediaUrls.current.delete(item.url); });
    const url = file ? URL.createObjectURL(file) : null;
    if (url) localMediaUrls.current.add(url);
    setSelectedImages(file ? [{ file, url }] : []);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (botLoadingMessage || voiceBusy || (!inputText.trim() && selectedImages.length === 0)) return;
    setSendError(null);

    // Prepare user message for UI
    const userMessage = {
      text: inputText,
      images: selectedImages.map((img) => img.url),
      messageType: isVideo(selectedImages[0]?.file) ? "file" : selectedImages.length ? "image" : "text",
      id: Date.now(),
      sender: "User",
    };
    setMessages((prev) => [
      ...prev,
      { ...userMessage, createdAt: new Date().toISOString() },
    ]);
    setTimeout(() => scrollToBottom(), 100);
    setInputText("");
    const transcriptionIds = voiceIds; setVoiceIds([]);

    setBotLoadingMessage(true);

    try {
      setSelectedImages([]);
      const data = await sendMessage({ uid: conversationUid, text: userMessage.text, image: selectedImages[0]?.file,
        ...(transcriptionIds.length ? { transcriptionIds } : {}) });
      const uid = data?.conversation?.uid || conversationUid;
      if (uid) setConversationUid(uid);
      notifyAssistantUpdated(uid, "floating-assistant");
      let synchronized = false;
      if (uid) {
        try {
          const thread = await api.get(`/assistant/conversations/${uid}/messages`);
          setMessages(thread.data?.messages || []);
          synchronized = true;
          selectedImages.forEach((item) => { URL.revokeObjectURL(item.url); localMediaUrls.current.delete(item.url); });
        } catch (syncError) {
          // Keep the local preview and append the answer below.
        }
      }
      if (!synchronized) {
        setMessages((prev) => [
          ...prev,
          {
            text: data.analysis,
            id: Date.now(),
            sender: "bot",
            createdAt: new Date().toISOString(),
          },
        ]);
      }

      setBotLoadingMessage(false);
      setTimeout(() => scrollToBottom(), 100);
    } catch (err) {
      console.error("Error fetching bot response:", err);
      setSendError(countingErrorMessage(err.response?.data?.message || err.code, currentLanguage()) || t("assistant_error"));
      setBotLoadingMessage(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setInputMessage(suggestion);
    setShowSuggestions(false);
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    return `${dateString.slice(0, 10).split("-").reverse().join("-")} ${dateString.slice(11, 16)}`;
  };

  if (!isOpen) {
    return (
      <div style={{ position: "fixed", bottom: "24px", right: "24px", zIndex: 3000 }}>
        <button
          onClick={toggleChatbot}
          className="sf-chat-toggle-btn"
          aria-label={t("assistant_open", "Ouvrir l'assistant")}
          title={t("assistant_open", "Ouvrir l'assistant")}
        >
          <MessageCircle size={24} />
        </button>
      </div>
    );
  }

  return (
    <div dir={i18n.dir ? i18n.dir() : "ltr"}>
      <div className="sf-chat-overlay" onClick={toggleChatbot} />

      <div className="sf-chat-drawer">
        {/* Header */}
        <div className="sf-chat-header">
          <button
            className="sf-chat-icon-btn"
            onClick={toggleChatbot}
            aria-label={t("back", "Retour")}
          >
            <ArrowLeft size={18} />
          </button>
          <div className="sf-chat-header-info">
            <Bot size={32} className="sf-chat-bot-avatar" aria-hidden="true" />
            <div className="sf-chat-title">{uiMessages.chatTitle}</div>
          </div>
          <button
            className="sf-chat-icon-btn"
            onClick={toggleChatbot}
            aria-label={t("close", "Fermer")}
          >
            <X size={18} />
          </button>
        </div>

        {/* Messages Container */}
        <div className="sf-chat-body">
          {messages.map((msg, idx) => (
            <div key={messageKey(msg, idx)} className={`sf-chat-message ${msg.sender === "User" ? "sf-chat-message-user" : "sf-chat-message-bot"}`}>
              {msg.sender === "User" ? (
                <div className="sf-chat-user-content">
                  {msg.images?.length > 0 && (
                    <div className="sf-chat-message-images">
                      {msg.images.map((imgUrl, idx) => msg.messageType === "file" ? (
                        <video key={idx} src={imgUrl} controls playsInline preload="metadata" className="sf-chat-message-image" />
                      ) : (
                        <img
                          key={idx}
                          src={imgUrl}
                          alt="uploaded"
                          className="sf-chat-message-image"
                        />
                      ))}
                    </div>
                  )}
                  {!msg.images?.length && !msg.mediaUrl && msg.messageType === "file" && (
                    <p>{mediaCopy(currentLanguage()).expired}</p>
                  )}
                  {msg?.mediaUrl?.length > 0 && (
                    <div className="sf-chat-message-images">
                      <img
                        src={`${process.env.REACT_APP_BASE_URL}${msg.mediaUrl}`}
                        alt="uploaded"
                        className="sf-chat-message-image"
                      />
                    </div>
                  )}
                  <div className="sf-chat-message-text sf-chat-user-text">{msg.text || msg.content}</div>
                  <div className="sf-chat-message-time">{formatTime(msg.createdAt)}</div>
                </div>
              ) : (
                <div className="sf-chat-bot-content">
                  <div className="sf-chat-message-text sf-chat-bot-text">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.text || msg.content}</ReactMarkdown>
                  </div>
                  <div className="sf-chat-message-time">{formatTime(msg.createdAt)}</div>
                  <div className="sf-chat-message-actions">
                    <button
                      className={`sf-chat-action-btn${speakingId === messageKey(msg, idx) ? " sf-chat-action-btn-active" : ""}`}
                      onClick={() => handleSpeak(msg, idx)}
                      disabled={ttsLoadingId === messageKey(msg, idx)}
                      title={t("listen_message")}
                      aria-label={t("listen_message")}
                    >
                      {ttsLoadingId === messageKey(msg, idx) ? (
                        <Loader2 size={14} className="sf-chat-tts-spinning" />
                      ) : speakingId === messageKey(msg, idx) ? (
                        <Square size={14} />
                      ) : (
                        <Volume2 size={14} />
                      )}
                    </button>
                    <button
                      className="sf-chat-action-btn"
                      aria-label={t("helpful", "Utile")}
                      title={t("helpful", "Utile")}
                    >
                      <ThumbsUp size={14} />
                    </button>
                    <button
                      className="sf-chat-action-btn"
                      aria-label={t("not_helpful", "Pas utile")}
                      title={t("not_helpful", "Pas utile")}
                    >
                      <ThumbsDown size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}

          {botLoadingMessage && (
            <div className="sf-chat-message sf-chat-message-bot">
              <div className="sf-chat-bot-content">
                <div className="sf-chat-typing-indicator">
                  <div className="sf-chat-typing-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Container */}
        <div className="sf-chat-input-container">
          {sendError && <p role="alert">{sendError}</p>}
          <AssistantAttachment file={selectedImages[0]?.file} onChange={selectMedia}
            disabled={botLoadingMessage} language={currentLanguage()} />
          <div className="sf-chat-form">
            <div className="sf-chat-input-wrapper">
              <input
                dir="auto"
                type="text"
                placeholder={t("whatDoYouWantToKnow")}
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                className="sf-chat-input"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend(e);
                  }
                }}
              />
            </div>

            <div className="sf-chat-actions">
              <div className="sf-chat-action-buttons">
                <AssistantVoice disabled={botLoadingMessage || ttsLoadingId != null} language={currentLanguage()} resetKey={`${isOpen}:${conversationUid || "new"}`}
                  onBusyChange={(value) => { setVoiceBusy(value); if (value) stopSpeaking(); }} onTranscript={(text, id) => {
                    setInputText((value) => `${value.trim()}${value.trim() ? " " : ""}${text}`);
                    if (id) setVoiceIds((ids) => [...ids, id].slice(-10));
                  }} />

              </div>

              <button
                type="button"
                className="sf-chat-send-button"
                disabled={botLoadingMessage || voiceBusy || (!inputText.trim() && selectedImages.length === 0)}
                onClick={handleSend}
                aria-label={t("send")}
                title={t("send")}
              >
                <Send size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chatbot;
